import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import OrderingPage from "@/pages/OrderingPage";
import OrderHistoryPage from "@/pages/OrderHistoryPage";
import SalesPage from "@/pages/SalesPage";
import CashReportingPage from "@/pages/CashReportingPage";
import AdminPage from "@/pages/AdminPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProfilePage from "@/pages/ProfilePage";

// Operations Manager Pages
import OpsLoginPage from "@/pages/ops/OpsLoginPage";
import OpsDashboard from "@/pages/ops/OpsDashboard";
import OpsOrdersPage from "@/pages/ops/OpsOrdersPage";
import OpsOrderDetailPage from "@/pages/ops/OpsOrderDetailPage";
import OpsLayout from "@/pages/ops/OpsLayout";
import OpsLocations from "@/pages/ops/OpsLocations";
import OpsDispatches from "@/pages/ops/OpsDispatches";
import OpsCashCollection from "@/pages/ops/OpsCashCollection";
import OpsChecklist from "@/pages/ops/OpsChecklist";
import OpsIncidents from "@/pages/ops/OpsIncidents";
import OpsManagementIssues from "@/pages/ops/OpsManagementIssues";
import OpsInventory from "@/pages/ops/OpsInventory";

// Components
import FloatingPOSButton from "@/components/FloatingPOSButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add axios interceptor to include session token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to save session token
export const saveSessionToken = (token) => {
  if (token) {
    localStorage.setItem('session_token', token);
  }
};

// Helper to clear session token
export const clearSessionToken = () => {
  localStorage.removeItem('session_token');
};

// Auth Callback Component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, {
          session_id: sessionId,
        });

        // Save session token to localStorage for mobile support
        if (response.data.session_token) {
          saveSessionToken(response.data.session_token);
        }

        // Clear the hash and navigate to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: response.data } });
      } catch (error) {
        console.error("Auth callback error:", error);
        const errorMessage = error.response?.data?.detail || "Authentication failed";
        navigate("/login", { state: { error: errorMessage } });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if user data passed from login or AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      // Clear state to prevent it from being used again on refresh
      window.history.replaceState({}, document.title);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.log("Auth check failed, redirecting to login");
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Clone children and pass user prop
  return (
    <>
      {children({ user, setUser })}
      <FloatingPOSButton />
    </>
  );
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL hash (from Google OAuth)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/ordering"
        element={
          <ProtectedRoute>
            {({ user }) => <OrderingPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            {({ user }) => <OrderHistoryPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            {({ user }) => <SalesPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/cash-reporting"
        element={
          <ProtectedRoute>
            {({ user }) => <CashReportingPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            {({ user }) => <NotificationsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <ProfilePage user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      
      {/* Operations Manager Routes */}
      <Route path="/ops/login" element={<OpsLoginPage />} />
      <Route path="/ops" element={<OpsLayout />}>
        <Route path="dashboard" element={<OpsDashboardWrapper />} />
        <Route path="locations" element={<OpsLocationsWrapper />} />
        <Route path="dispatches" element={<OpsDispatchesWrapper />} />
        <Route path="cash-collection" element={<OpsCashCollectionWrapper />} />
        <Route path="checklist" element={<OpsChecklistWrapper />} />
        <Route path="incidents" element={<OpsIncidentsWrapper />} />
        <Route path="issues" element={<OpsManagementIssuesWrapper />} />
        <Route path="inventory" element={<OpsInventoryWrapper />} />
        <Route path="orders" element={<OpsOrdersPage />} />
        <Route path="orders/:orderId" element={<OpsOrderDetailPage />} />
      </Route>
      
      <Route path="/" element={<LoginPage />} />
    </Routes>
  );
}

// Wrapper components to pass user from outlet context
function OpsDashboardWrapper() {
  const { user } = useOutletContext();
  return <OpsDashboard user={user} />;
}

function OpsLocationsWrapper() {
  const { user } = useOutletContext();
  return <OpsLocations user={user} />;
}

function OpsDispatchesWrapper() {
  const { user } = useOutletContext();
  return <OpsDispatches user={user} />;
}

function OpsCashCollectionWrapper() {
  const { user } = useOutletContext();
  return <OpsCashCollection user={user} />;
}

function OpsChecklistWrapper() {
  const { user } = useOutletContext();
  return <OpsChecklist user={user} />;
}

function OpsIncidentsWrapper() {
  const { user } = useOutletContext();
  return <OpsIncidents user={user} />;
}

function OpsManagementIssuesWrapper() {
  const { user } = useOutletContext();
  return <OpsManagementIssues user={user} />;
}

function OpsInventoryWrapper() {
  const { user } = useOutletContext();
  return <OpsInventory user={user} />;
}

function App() {
  return (
    <div className="App lotus-bg min-h-screen">
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" />
      </CartProvider>
    </div>
  );
}

export default App;
